import { History } from "lucide-react";
import moment from "moment/moment";
import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UsersServices, User, VideoProject, VideoProjectServices } from "@/services/bedrock";

export const videoProjectEventLogsQueryKey = (id: number | string) => ["videoProjectEventLogs", String(id)] as const;

const EVENT_LABELS: Record<string, string> = {
  start_editing: "Started editing",
  stop_editing: "Stopped editing",
  submit_for_testing: "Submitted for testing",
  complete: "Completed",
  return_to_editing: "Returned to editing",
  start_testing: "Started testing",
  stop_testing: "Stopped testing",
  reject_qa: "Rejected QA",
  approve_qa: "Approved QA",
  request_revision: "Requested revision",
  schedule: "Scheduled",
  fail_post: "Post failed",
  succeed_post: "Posted",
};

interface VideoProjectHistoryPanelProps {
  videoProjectId: number;
}

function formatStatusValue(value?: string | null): string {
  if (!value) return "Unknown";
  return (
    VideoProject.StatusMeta[value as VideoProject.Status]?.label ||
    value
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/^\w/, c => c.toUpperCase())
  );
}

export function VideoProjectHistoryPanel({ videoProjectId }: VideoProjectHistoryPanelProps) {
  const { data: project, isLoading } = useQuery({
    queryKey: videoProjectEventLogsQueryKey(videoProjectId),
    queryFn: () => VideoProjectServices.show(videoProjectId, { eventLogs: true }),
  });

  const { data: editorsData } = useQuery({
    queryKey: ["editors"],
    queryFn: () => UsersServices.getUsers({ userType: User.Type.EDITOR }),
    staleTime: 1000 * 60 * 30,
  });

  const usersById = React.useMemo(() => {
    const map = new Map<number, User>();
    (editorsData?.content || []).forEach(user => map.set(user.id, user));
    return map;
  }, [editorsData]);

  const eventLogs = project?.eventLogs || [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row justify-between items-center w-full select-none">
        <label className="sectionHeader font-semibold text-slate-800 text-sm flex items-center gap-1.5">
          <History className="size-4.5 text-blue-600" />
          History
        </label>
      </div>
      <div className="videoProjectContainer topContainer flex flex-col gap-3 bg-slate-50/50 p-5 rounded-xl border border-slate-200">
        {isLoading && <span className="text-xs text-slate-400">Loading history…</span>}
        {!isLoading && eventLogs.length === 0 && <span className="text-xs text-slate-400">No status events yet.</span>}
        {eventLogs.map((item, index) => {
          const failed = item.succeeded === false;
          const actorName =
            item.actorName || (item.actorUserId ? usersById.get(item.actorUserId)?.fullName : null) || "System";
          const eventLabel = item.event ? EVENT_LABELS[item.event] || item.event.replace(/_/g, " ") : "Event";

          return (
            <div
              key={item.id ?? `${item.createdDate}-${index}`}
              className="flex flex-col gap-1 pb-3 border-b border-slate-100 last:border-b-0 last:pb-0"
            >
              <div className="flex flex-row justify-between items-start gap-2">
                <span className={`text-sm font-medium ${failed ? "text-rose-700" : "text-slate-700"}`}>
                  {failed ? `Failed: ${eventLabel}` : eventLabel}
                </span>
                <span className="text-slate-400 text-xs font-medium shrink-0">
                  {item.createdDate ? moment(item.createdDate).format("MMM D, YYYY [at] h:mm a") : ""}
                </span>
              </div>
              <span className="text-xs text-slate-500">{actorName}</span>
              <span className="text-xs text-slate-600">
                {formatStatusValue(item.fromStatus)}
                {item.toStatus ? ` → ${formatStatusValue(item.toStatus)}` : ""}
              </span>
              {failed && item.error && <span className="text-xs text-rose-600">{item.error}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function useInvalidateVideoProjectEventLogs() {
  const queryClient = useQueryClient();
  return (id: number | string) => queryClient.invalidateQueries({ queryKey: videoProjectEventLogsQueryKey(id) });
}
